/**
 * Audit filtering utilities
 * Filters audit events at the change level (not version level)
 */

import type { AuditFieldConfig } from './audit-config'
import type { AuditHistoryFilters, AuditRecord, AuditEvent, SectionGroup, ChangeGroup, FieldChange } from '@/types/ui/audit'

/**
 * Main filtering function - filters events and their audits based on all filter criteria
 */
export function filterEvents(
  events: AuditEvent[],
  filters: AuditHistoryFilters,
  config: AuditFieldConfig
): AuditEvent[] {
  return events
    .map(event => ({
      ...event,
      audits: event.audits
        .map(audit => filterAudit(audit, filters, config))
        .filter(audit => shouldShowAudit(audit, filters, config))
    }))
    .filter(event => event.audits.length > 0)
}

/**
 * Determine if an audit should be shown based on filter state and visible sections
 */
function shouldShowAudit(
  audit: AuditRecord,
  filters: AuditHistoryFilters,
  config: AuditFieldConfig
): boolean {
  // Always hide audits that don't pass audit-level filters (status, user, action)
  if (!passesAuditLevelFilters(audit, filters, config)) {
    return false
  }
  
  // If user matches search query, show this audit even if no sections are visible
  if (auditUserMatchesSearch(audit, filters.searchQuery || '')) {
    return true
  }
  
  // If search query is active but didn't match user, hide if no sections visible
  // (this means search didn't match any field changes either)
  if (filters.searchQuery && filters.searchQuery.trim() !== '' && audit.sectionGroups.length === 0) {
    return false
  }
  
  // If section filter is active, hide audits with no visible sections
  const hasSectionFilter = (filters.sectionFilter && filters.sectionFilter.length > 0)
  if (hasSectionFilter && audit.sectionGroups.length === 0) {
    return false
  }
  
  // Otherwise show the audit (even if no sections - ChangeHistoryTable will render as non-expandable)
  return true
}

export function filterAudit(
  audit: AuditRecord,
  filters: AuditHistoryFilters,
  config: AuditFieldConfig
): AuditRecord {
  // Filter sectionGroups based on change-level filters (search, section, showSystemFields)
  const filteredSectionGroups = audit.sectionGroups
    .map(sectionGroup => filterSectionGroup(sectionGroup, filters))
    .filter((section): section is SectionGroup => section !== null)

  return {
    ...audit,
    sectionGroups: filteredSectionGroups
  }
}

function passesAuditLevelFilters(
  audit: AuditRecord,
  filters: AuditHistoryFilters,
  config: AuditFieldConfig
): boolean {
  // User filter
  if (filters.userFilter && filters.userFilter.length > 0) {
    if (!filters.userFilter.includes(audit.modifiedBy.displayName)) {
      return false
    }
  }

  // Action filter
  if (filters.actionFilter && filters.actionFilter.length > 0) {
    const action = config.formatAction(audit.action)
    if (!filters.actionFilter.includes(action)) {
      return false
    }
  }

  // Status filter
  if (filters.statusFilter && filters.statusFilter.length > 0) {
    if (!filters.statusFilter.includes(audit.status)) {
      return false
    }
  }

  return true
}

/**
 * Check if search query matches the audit's user
 */
function auditUserMatchesSearch(
  audit: AuditRecord,
  searchQuery: string
): boolean {
  if (!searchQuery || searchQuery.trim() === '') {
    return false
  }
  const query = searchQuery.toLowerCase()
  return audit.modifiedBy.displayName.toLowerCase().includes(query)
}

function filterSectionGroup(
  sectionGroup: SectionGroup,
  filters: AuditHistoryFilters
): SectionGroup | null {
  // System fields filter (hide System section unless showSystemFields is true)
  if (sectionGroup.section === "System" && !filters.showSystemFields) {
    return null
  }

  // Section filter
  if (filters.sectionFilter && filters.sectionFilter.length > 0) {
    if (!filters.sectionFilter.includes(sectionGroup.section)) {
      return null
    }
  }

  // Filter changeGroups
  const filteredChangeGroups = sectionGroup.changeGroups
    .map(changeGroup => filterChangeGroup(changeGroup, filters))
    .filter((group): group is ChangeGroup => group !== null)

  if (filteredChangeGroups.length === 0) return null

  return {
    ...sectionGroup,
    changeGroups: filteredChangeGroups
  }
}

function filterChangeGroup(
  changeGroup: ChangeGroup,
  filters: AuditHistoryFilters
): ChangeGroup | null {
  const filteredChanges = changeGroup.changes.filter(change => 
    passesSearchFilter(change, filters.searchQuery)
  )

  if (filteredChanges.length === 0) return null

  return {
    ...changeGroup,
    changes: filteredChanges
  }
}

function passesSearchFilter(change: FieldChange, searchQuery?: string): boolean {
  if (!searchQuery) return true

  const query = searchQuery.toLowerCase()
  return (
    change.fieldName.toLowerCase().includes(query) ||
    change.displayName.toLowerCase().includes(query) ||
    change.formattedOldValue.toLowerCase().includes(query) ||
    change.formattedNewValue.toLowerCase().includes(query) ||
    (change.parentLabel?.toLowerCase().includes(query) ?? false)
  )
}

